import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, Outlet } from 'react-router-dom';
import './App.css';
import './components/public/public.css';
import { AuthProvider } from './auth/AuthProvider';
import Header from './components/public/Header';
import Footer from './components/public/Footer';
import Hero from './components/public/Hero';
import About from './components/public/About';
import Cars from './components/public/Cars';
import EventsTeaser from './components/public/EventsTeaser';
import GalleryTeaser from './components/public/GalleryTeaser';
import Instagram from './components/public/Instagram';
import Faq from './components/public/Faq';
import CrewBand from './components/public/CrewBand';
import Contact from './components/public/Contact';
import RouteError from './components/RouteError';
import InstallBanner from './components/pwa/InstallBanner';
import UpdateBanner from './components/pwa/UpdateBanner';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PublicOnlyRoute from './components/auth/PublicOnlyRoute';
import AdminRoute from './components/auth/AdminRoute';
import { usePageView } from './hooks/usePageView';
import OrganizerRoute from './components/auth/OrganizerRoute';
import CrewRoute from './components/auth/CrewRoute';

// Code-Splitting: Alles hinter dem Login und die sekundären öffentlichen
// Seiten sind lazy. Eager bleiben nur Startseiten-Bausteine, Routen-Gates und
// der AuthProvider. Neue Routen-Komponenten IMMER als lazy() ergänzen.
const EventsPage = lazy(() => import('./components/public/EventsPage'));
const EventPage = lazy(() => import('./components/public/EventPage'));
// Die Adresse ist der Slug und zugleich die Dokument-ID des Stammtischs -
// deshalb kommt die Seite ohne Aufloesung und ohne zusaetzlichen Index aus.
const StammtischPage = lazy(() => import('./components/public/StammtischPage'));
const ConnectPage = lazy(() => import('./components/member/ConnectPage'));
const MerchPage = lazy(() => import('./components/member/MerchPage'));
const WikiPage = lazy(() => import('./components/member/WikiPage'));
const GalleryPage = lazy(() => import('./components/public/GalleryPage'));
const JoinPage = lazy(() => import('./components/public/JoinPage'));
const Login = lazy(() => import('./components/auth/Login'));
const Register = lazy(() => import('./components/auth/Register'));
const Impressum = lazy(() => import('./components/legal/Impressum'));
const Privacy = lazy(() => import('./components/legal/Privacy'));
const MemberLayout = lazy(() => import('./components/member/MemberLayout'));
const MemberDashboard = lazy(() => import('./components/member/MemberDashboard'));
const MemberProfile = lazy(() => import('./components/member/MemberProfile'));
const MemberMap = lazy(() => import('./components/member/MemberMap'));
const CrewProfile = lazy(() => import('./components/member/CrewProfile'));
const AnnouncementBoard = lazy(() => import('./components/member/AnnouncementBoard'));
const CrewList = lazy(() => import('./components/member/CrewList'));
const AlbumPage = lazy(() => import('./components/public/AlbumPage'));
const EventDetail = lazy(() => import('./components/member/EventDetail'));
// Der Clubkalender als eigene Seite. Er bringt Kopfzeile, Umschalter
// (Liste/Monat) und Kategorie-Filter selbst mit - ein Seitenrahmen darum
// waere eine leere Huelle.
const MemberEvents = lazy(() => import('./components/member/EventCalendar'));
const ReleaseNotesPage = lazy(() => import('./components/member/ReleaseNotesPage'));
const OrganizerPage = lazy(() => import('./components/member/OrganizerPage'));
const MemberIdeas = lazy(() => import('./components/member/MemberIdeas'));
const OrganizerInvitePage = lazy(() => import('./components/member/OrganizerInvitePage'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
// Eigene Seite statt siebter Reiter im Admin-Bereich: Dort geht es um den
// Club, hier um die Seite selbst - und die Reiterleiste hat mit sechs
// Einträgen ihre Breite erreicht.
const IdeenManager = lazy(() => import('./components/admin/IdeenManager'));
const ActivityLog = lazy(() => import('./components/admin/ActivityLog'));
const UsageDashboard = lazy(() => import('./components/admin/UsageDashboard'));
const MerchRequests = lazy(() => import('./components/admin/MerchRequests'));

function ChunkLoader() {
  return (
    <div className="mt-loading">
      <div className="mt-spinner" />
    </div>
  );
}

/** Gemeinsames Seitengerüst: Header, Inhalt, Footer. */
function Layout() {
  // Hier und nur hier wird gezählt (v0.25.0). In den einzelnen Seiten wäre
  // ein vergessener Aufruf eine Lücke, die erst auffiele, wenn die Zahl
  // unerklärlich niedrig ist.
  usePageView();

  return (
    <div className="mt-app">
      <Header />
      <main className="mt-main" id="inhalt">
        <Suspense fallback={<ChunkLoader />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

function HomePage() {
  return (
    <>
      <Hero />
      <About />
      <Cars />
      <EventsTeaser />
      <GalleryTeaser />
      <Instagram />
      <Faq />
      <CrewBand />
      <Contact />
    </>
  );
}

// Alle Routen hängen unter EINER pathless Wurzel mit errorElement - so fängt
// RouteError die ChunkLoadErrors aller Lazy-Routen ab. Eine klassische
// ErrorBoundary greift dafür NICHT: Der Data-Router fängt Routen-Fehler
// intern ab, bevor sie zu einer Boundary durchbrechen.
const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      {
        element: <Layout />,
        children: [
          {
            // Die Startseite ist die Seite fuer Gaeste und Interessenten.
            // Ein angemeldetes Mitglied gehoert in seine Club-Zentrale -
            // ueber „Oeffentliche Seite" im Menue kommt es hierher zurueck
            // (allowEscape, siehe utils/publicHome.js).
            //
            // showWhileLoading: Kein Spinner auf der Startseite. Ihr Inhalt
            // ist die einzige eager gebuendelte Route, damit der erste
            // Eindruck sofort steht - der Spinner traefe jeden Besucher.
            path: '/',
            element: (
              <PublicOnlyRoute showWhileLoading allowEscape>
                <HomePage />
              </PublicOnlyRoute>
            ),
          },
          { path: '/termine', element: <EventsPage /> },
          { path: '/termine/:eventId', element: <EventPage /> },
          { path: '/stammtisch/:slug', element: <StammtischPage /> },
          { path: '/galerie', element: <GalleryPage /> },
          { path: '/galerie/:albumId', element: <AlbumPage /> },
          {
            // Wer angemeldet ist, hat sich laengst registriert - der alte
            // Link aus einem Lesezeichen fuehrt deshalb in den eigenen
            // Bereich statt auf die Registrierungs-Seite.
            path: '/mitglied-werden',
            element: (
              <PublicOnlyRoute>
                <JoinPage />
              </PublicOnlyRoute>
            ),
          },
          { path: '/login', element: <Login /> },
          {
            // Gleiche Ueberlegung wie bei „Mitglied werden": Wer angemeldet
            // ist, hat sich laengst registriert.
            path: '/registrieren',
            element: (
              <PublicOnlyRoute>
                <Register />
              </PublicOnlyRoute>
            ),
          },
          { path: '/impressum', element: <Impressum /> },
          { path: '/datenschutz', element: <Privacy /> },
          {
            // Das Gate sitzt an der Elternroute - jede Unterseite ist damit
            // automatisch geschuetzt, ohne es einzeln zu wiederholen.
            path: '/intern',
            element: (
              <ProtectedRoute>
                <MemberLayout />
              </ProtectedRoute>
            ),
            children: [
              { index: true, element: <MemberDashboard /> },
              { path: 'aushang', element: <AnnouncementBoard /> },
              { path: 'crew', element: <CrewList /> },
              {
                // „Die Crew" ist ein eigener Reiter und kein Filter in der
                // Mitgliederliste: Er soll fuer gewoehnliche Mitglieder gar
                // nicht auftauchen, und ein Filter, den man nicht sieht, ist
                // keiner.
                path: 'die-crew',
                element: (
                  <CrewRoute crewOnly>
                    <CrewList onlyCrew />
                  </CrewRoute>
                ),
              },
              { path: 'vernetzen', element: <ConnectPage /> },
              // Der Kalender hat wieder eine eigene Seite - zwischen v0.9.14
              // und diesem Umbau stand er auf der Uebersicht. Der alte Pfad
              // bleibt als Weiterleitung: Er steckt in Lesezeichen und in der
              // installierten App.
              { path: 'kalender', element: <Navigate to="/intern/termine" replace /> },
              { path: 'termine', element: <MemberEvents /> },
              { path: 'termine/:eventId', element: <EventDetail /> },
              {
                // Zusätzliches Gate: Das Anlegen von Terminen ist an die
                // Organisator-Rolle gebunden, alles andere unter /intern
                // nicht.
                path: 'organisieren',
                element: (
                  <OrganizerRoute>
                    <OrganizerPage />
                  </OrganizerRoute>
                ),
              },
              {
                // Zusaetzliches Gate: Die Aenderungsliste ist fuer die, die
                // den Club machen - Crew, Orga, Admin.
                path: 'neuerungen',
                element: (
                  <CrewRoute>
                    <ReleaseNotesPage />
                  </CrewRoute>
                ),
              },
              { path: 'karte', element: <MemberMap /> },
              // Kein zusaetzliches Gate: Vorschlagen darf jedes freigegebene
              // Mitglied - das Gate an /intern reicht.
              { path: 'ideen', element: <MemberIdeas /> },
              // Ebenfalls ohne zusaetzliches Gate: Bewerben darf jedes
              // freigegebene Mitglied, ein Orga sieht dort seinen Stand.
              { path: 'orga-werden', element: <OrganizerInvitePage /> },
              // Dieselben Komponenten wie unter /galerie, nur ohne eigenen
              // Seitenrahmen und mit Links, die im Mitgliederbereich bleiben.
              // Die Fotos sind ohnehin oeffentlich - es geht darum, dass ein
              // Mitglied sie findet, ohne seinen Bereich zu verlassen.
              { path: 'galerie', element: <GalleryPage embedded /> },
              { path: 'galerie/:albumId', element: <AlbumPage embedded /> },
              { path: 'merch', element: <MerchPage /> },
              { path: 'wiki', element: <WikiPage /> },
              { path: 'profil', element: <MemberProfile /> },
              { path: 'crew/:uid', element: <CrewProfile /> },
            ],
          },
          {
            path: '/admin',
            element: (
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            ),
          },
          {
            path: '/admin/ideen',
            element: (
              <AdminRoute>
                <IdeenManager />
              </AdminRoute>
            ),
          },
          {
            path: '/admin/log',
            element: (
              <AdminRoute>
                <ActivityLog />
              </AdminRoute>
            ),
          },
          {
            path: '/admin/nutzung',
            element: (
              <AdminRoute>
                <UsageDashboard />
              </AdminRoute>
            ),
          },
          {
            path: '/admin/merch-anfragen',
            element: (
              <AdminRoute>
                <MerchRequests />
              </AdminRoute>
            ),
          },
          { path: '*', element: <RouteError /> },
        ],
      },
    ],
  },
]);

export default function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
      {/* Außerhalb des Routers: Beide Leisten gelten für die ganze App und
          sollen bei einem Seitenwechsel nicht neu aufgebaut werden. */}
      <InstallBanner />
      <UpdateBanner />
    </AuthProvider>
  );
}
